import {
  isAssetUrl,
  isChunkOrAssetFailure,
  tryReloadForChunkError
} from '@/services/helpers/ChunkLoadRecovery.js'
import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent'

// --- Constants
const NEW_RELIC_TIMEOUT_MS = 5000
const NEW_RELIC_CHECK_INTERVAL_MS = 100
const NEW_RELIC_MAX_ATTEMPTS =
  NEW_RELIC_TIMEOUT_MS / NEW_RELIC_CHECK_INTERVAL_MS
const MAX_ATTR_VALUE_LENGTH = 2000
const MAX_ATTR_KEYS = 64
const MAX_EARLY_ERROR_QUEUE_SIZE = 50

const VUE_ERROR_PREFIX = 'Vue Error:'
const SCRIPT_ERROR_MESSAGES = ['Script error.', 'Script error'] as const
const RESIZE_OBSERVER_MESSAGE =
  'ResizeObserver loop completed with undelivered notifications.'

/** Sources that get a message prefix in NR so they form separate error groups. */
const CHECKOUT_SOURCES = [
  'checkout_edit_address',
  'checkout_opc',
  'checkout_regular',
  'checkout_guest'
] as const

function isScriptErrorMessage(msg: string | null): boolean {
  return (
    msg !== null && (SCRIPT_ERROR_MESSAGES as readonly string[]).includes(msg)
  )
}

function isResizeObserverMessage(msg: string | null): boolean {
  return msg === RESIZE_OBSERVER_MESSAGE
}

// --- Core reporting
interface EarlyErrorItem {
  error: Error
  extraData: Record<string, unknown>
}

/** Errors reported before the agent is ready; flushed when agent becomes available. */
const earlyErrorQueue: EarlyErrorItem[] = []

declare global {
  interface Window {
    newrelic?: {
      noticeError: (error: Error, extraData?: Record<string, any>) => void
      setUserId: (value: string | null) => void
    }
    __PINIA__?: {
      state?: {
        value?: {
          customer?: {
            customerProfile?: {
              customer_id?: string
            }
          }
        }
      }
    }
  }
}

interface NewRelicOptions {
  init: {
    distributed_tracing: { enabled: boolean }
    privacy: { cookies_enabled: boolean }
  }
  info: {
    beacon: string
    errorBeacon: string
    licenseKey: string
    applicationID: string
    sa: number
  }
  loader_config: {
    accountID: string
    trustKey: string
    agentID: string
    licenseKey: string
    applicationID: string
  }
}

const safeStringify = (obj: unknown): string => {
  const seen = new WeakSet()
  return JSON.stringify(
    obj,
    (_, value) => {
      if (typeof value === 'function') return '[Function]'
      if (value === globalThis.window) return '[Window]'
      if (value === document) return '[Document]'
      if (value && typeof value === 'object') {
        if (seen.has(value)) return '[Circular]'
        seen.add(value)
      }
      return value
    },
    2
  )
}

/** Trims string values and limits keys so payloads stay within New Relic limits. */
const trimExtraData = (
  data: Record<string, unknown>
): Record<string, unknown> => {
  const entries = Object.entries(data)
  const trimmed: Record<string, unknown> = {}
  for (let i = 0; i < Math.min(entries.length, MAX_ATTR_KEYS); i++) {
    const [key, value] = entries[i]
    if (value === null || value === undefined) {
      trimmed[key] = value
      continue
    }
    if (typeof value === 'string') {
      trimmed[key] =
        value.length > MAX_ATTR_VALUE_LENGTH
          ? value.slice(0, MAX_ATTR_VALUE_LENGTH) + '…'
          : value
      continue
    }
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      const str = safeStringify(value)
      trimmed[key] =
        str.length > MAX_ATTR_VALUE_LENGTH
          ? str.slice(0, MAX_ATTR_VALUE_LENGTH) + '…'
          : str
      continue
    }
    trimmed[key] = value
  }
  return trimmed
}

/**
 * Report error to New Relic. If the agent is not ready yet, the error is queued and sent when it becomes available.
 */
const reportToNewRelic = (
  error: Error,
  extraData: Record<string, unknown>
): void => {
  const capped = trimExtraData(extraData)
  if (globalThis.window?.newrelic?.noticeError) {
    globalThis.window.newrelic.noticeError(error, capped)
  } else {
    if (earlyErrorQueue.length >= MAX_EARLY_ERROR_QUEUE_SIZE) {
      earlyErrorQueue.shift()
    }
    earlyErrorQueue.push({ error, extraData: capped })
  }
}

const flushEarlyErrorQueue = (): void => {
  const noticeError = globalThis.window?.newrelic?.noticeError
  if (!noticeError) return
  while (earlyErrorQueue.length > 0) {
    const item = earlyErrorQueue.shift()
    if (item) noticeError(item.error, item.extraData as Record<string, any>)
  }
}

/**
 * Set the New Relic user id for session grouping. Call when the customer is known (e.g. after login).
 * Pass null to clear (e.g. on logout).
 */
export function setNewRelicUserId(customerId: string | number | null): void {
  const setUserId = globalThis.window?.newrelic?.setUserId
  if (!setUserId) return
  setUserId(
    customerId !== null && customerId !== undefined ? String(customerId) : null
  )
}

/**
 * Report an error to New Relic explicitly. Use for paths where you need a stable source
 * (e.g. checkout_opc, checkout_guest) so NR shows a separate error group.
 * In NRQL filter by custom.source to get errors by flow.
 */
export function reportErrorToNewRelic(
  error: unknown,
  context?: { source?: string; [key: string]: unknown }
): void {
  if (import.meta.env.VITE_APP_NEW_RELIC_DEV_MODE === 'ON') return
  try {
    let err: Error
    if (error instanceof Error) {
      err = error
    } else {
      const msg =
        typeof error === 'object' && error !== null && 'message' in error
          ? String((error as { message?: unknown }).message)
          : String(error)
      err = new Error(msg)
    }
    const source = context?.source ?? 'explicit'
    const msg = String(err.message ?? '')
    const reportErr =
      source !== 'explicit' &&
      (CHECKOUT_SOURCES as readonly string[]).includes(source)
        ? Object.assign(new Error(`[${source}] ${msg}`), { stack: err.stack })
        : err

    const extra: Record<string, unknown> = {
      source,
      apiErrorMessage:
        msg.length > MAX_ATTR_VALUE_LENGTH
          ? `${msg.slice(0, MAX_ATTR_VALUE_LENGTH)}…`
          : msg,
      ...context
    }
    reportToNewRelic(reportErr, extra)
  } catch (e) {
    globalThis.window?.console?.error?.('reportErrorToNewRelic failed', e)
  }
}

// --- Console interceptor helpers
const OBJECT_ERROR_FIELDS: [string, string][] = [
  ['status', 'status'],
  ['statusCode', 'statusCode'],
  ['url', 'url'],
  ['code', 'code'],
  ['name', 'errorName'],
  ['cause', 'cause'],
  ['description', 'description']
]

function assignExtraDataFromObject(
  arg: Record<string, unknown>,
  extraData: Record<string, any>
): void {
  for (const [srcKey, destKey] of OBJECT_ERROR_FIELDS) {
    const val = arg[srcKey]
    if (val !== undefined) extraData[destKey] = val
  }
  const orig = arg.originalError as
    | { message?: string; stack?: string }
    | undefined
  if (orig !== undefined && orig !== null) {
    extraData.originalErrorMessage = orig.message
    extraData.originalErrorStack = orig.stack
  }
  extraData.rawError = safeStringify(arg)
}

const parseConsoleErrorArgs = (
  args: unknown[]
): {
  errorMessage: string | null
  errorObject: Error | null
  extraData: Record<string, any>
} => {
  let errorMessage: string | null = null
  let errorObject: Error | null = null
  const extraData: Record<string, any> = {}

  for (const arg of args) {
    if (arg instanceof Error) {
      errorObject = arg
      errorMessage = errorMessage ?? arg.message
      extraData.originalStack = arg.stack
      continue
    }
    if (typeof arg === 'string') {
      errorMessage = arg
      continue
    }
    if (typeof arg === 'object' && arg !== null) {
      const obj = arg as Record<string, unknown>
      if (obj.message) errorMessage = String(obj.message)
      assignExtraDataFromObject(obj, extraData)
      continue
    }
    if (typeof arg === 'number') extraData.numericValue = arg
    else if (typeof arg === 'boolean') extraData.booleanValue = arg
  }

  return { errorMessage, errorObject, extraData }
}

const buildExtraData = (args: unknown[]): Record<string, any> => {
  const extraData: Record<string, any> = {
    source: 'console.error',
    timestamp: new Date().toISOString(),
    url: globalThis.window.location.href,
    userAgent: navigator.userAgent,
    referrer: document.referrer,
    viewport: {
      width: globalThis.window.innerWidth,
      height: globalThis.window.innerHeight
    },
    arguments: (() => {
      const s = args
        .map((arg) => {
          if (typeof arg === 'object' && arg !== null) return '[Object]'
          return String(arg)
        })
        .join(', ')
      return s.length > MAX_ATTR_VALUE_LENGTH
        ? s.slice(0, MAX_ATTR_VALUE_LENGTH) + '…'
        : s
    })()
  }

  // Add customer ID if available
  try {
    const customerStore = globalThis.window.__PINIA__?.state?.value?.customer
    if (customerStore?.customerProfile?.customer_id) {
      extraData.customer_id = customerStore.customerProfile.customer_id
    }
  } catch (e) {
    console.debug('error getting customer data for error logging', e)
  }

  // Add performance metrics if available
  if (performance?.getEntriesByType) {
    const nav = performance.getEntriesByType(
      'navigation'
    )[0] as PerformanceNavigationTiming
    if (nav) {
      extraData.pageLoadTime = nav.loadEventEnd - nav.startTime
      extraData.domReadyTime = nav.domContentLoadedEventEnd - nav.startTime
    }
  }

  return extraData
}

// --- Console error interceptor (every console.error() → NR with source 'console.error')
const setupConsoleErrorInterceptor = () => {
  if (globalThis.window === undefined) {
    console.error('window is undefined, cannot setup console error interceptor')
    return
  }
  const originalConsoleError = console.error
  if (import.meta.env.VITE_APP_NEW_RELIC_DEV_MODE !== 'ON') {
    if (
      originalConsoleError.toString().includes('setupConsoleErrorInterceptor')
    ) {
      console.warn(
        'Console.error appears to be already overridden, skipping New Relic interceptor setup'
      )
      return
    }
  }

  console.error = (...args) => {
    originalConsoleError.apply(console, args)

    try {
      // Skip NR: already sent by Vue error handler with richer context
      if (
        args.length >= 2 &&
        args[0] === VUE_ERROR_PREFIX &&
        args[1] instanceof Error
      ) {
        return true
      }

      const {
        errorMessage,
        errorObject,
        extraData: parsedExtraData
      } = parseConsoleErrorArgs(args)

      // Create error object if none exists
      const finalErrorObject =
        errorObject || new Error(errorMessage ?? 'Console Error')
      if (!errorObject) {
        finalErrorObject.stack = new Error('Stack capture').stack
      }

      // Build comprehensive extra data
      const finalExtraData = {
        ...parsedExtraData,
        ...buildExtraData(args)
      }

      if (
        isScriptErrorMessage(errorMessage) ||
        isScriptErrorMessage(finalErrorObject.message)
      ) {
        finalExtraData.isThirdPartyScriptError = true
        finalExtraData.filtered = true
      }

      if (isChunkOrAssetFailure(finalErrorObject)) {
        finalExtraData.recoverableChunkLoad = true
        return true
      }

      if (
        isResizeObserverMessage(errorMessage) ||
        isResizeObserverMessage(finalErrorObject.message)
      ) {
        return true
      }

      if (import.meta.env.VITE_NEW_RELIC_DEBUG === 'ON') {
        console.debug('logging to new relic', finalErrorObject, finalExtraData)
      }

      reportToNewRelic(finalErrorObject, finalExtraData)
      return true
    } catch (e) {
      originalConsoleError('Failed to log to New Relic:', e)
      // Fallback: send something so the event is never lost (interceptor parse/build failed)
      try {
        const err = args.find((a): a is Error => a instanceof Error)
        const fallbackErr = err ?? new Error(String(args[0] ?? 'Console error'))
        if (!err) fallbackErr.stack = new Error('Stack capture').stack
        reportToNewRelic(fallbackErr, {
          source: 'console.error',
          interceptorFallback: true,
          parseError: String((e as Error)?.message ?? e)
        })
      } catch {
        // Intentionally ignore fallback report failure
      }
      return false
    }
  }
}

// --- Global handlers & loader
const setupGlobalErrorHandlers = (): void => {
  if (globalThis.window === undefined) return

  globalThis.window.onerror = (message, source, _lineno, _colno, error) => {
    const msg = typeof message === 'string' ? message : null
    if (isScriptErrorMessage(msg) && !source) return true
    if (isResizeObserverMessage(msg)) return true
    if (source && isAssetUrl(source)) {
      tryReloadForChunkError()
      return true
    }
    console.error(error ?? message)
    return false
  }
}

function waitForNewRelic(): Promise<void> {
  return new Promise((resolve) => {
    let attempts = 0

    const check = (): void => {
      attempts++
      if (globalThis.window?.newrelic?.noticeError) {
        setupConsoleErrorInterceptor()
        flushEarlyErrorQueue()
        resolve()
        return
      }
      if (attempts >= NEW_RELIC_MAX_ATTEMPTS) {
        console.warn(
          `New Relic failed to load within ${NEW_RELIC_TIMEOUT_MS}ms timeout, setting up interceptor anyway`
        )
        setupConsoleErrorInterceptor()
        flushEarlyErrorQueue()
        resolve()
        return
      }
      setTimeout(check, NEW_RELIC_CHECK_INTERVAL_MS)
    }

    check()
  })
}

export const loadNewRelic = async (): Promise<void> => {
  if (import.meta.env.VITE_APP_NEW_RELIC_DEV_MODE === 'ON') {
    return
  }

  const licenseKey = `${import.meta.env.VITE_NEW_RELIC_LICENSE_KEY ?? ''}`
  const applicationID = `${import.meta.env.VITE_NEW_RELIC_APPLICATION_ID ?? ''}`
  if (
    !licenseKey ||
    !applicationID ||
    licenseKey === 'undefined' ||
    applicationID === 'undefined'
  ) {
    console.warn(
      'New Relic: missing or invalid VITE_NEW_RELIC_LICENSE_KEY or VITE_NEW_RELIC_APPLICATION_ID, skipping agent init'
    )
    return
  }

  const options: NewRelicOptions = {
    init: {
      distributed_tracing: { enabled: true },
      privacy: { cookies_enabled: true }
    },
    info: {
      beacon: 'bam.nr-data.net',
      errorBeacon: 'bam.nr-data.net',
      licenseKey,
      applicationID,
      sa: 1
    },
    loader_config: {
      accountID: `${import.meta.env.VITE_NEW_RELIC_ACCOUNT_ID ?? ''}`,
      trustKey: `${import.meta.env.VITE_NEW_RELIC_TRUST_KEY ?? ''}`,
      agentID: `${import.meta.env.VITE_NEW_RELIC_AGENT_ID ?? ''}`,
      licenseKey,
      applicationID
    }
  }

  const _browserAgent = new BrowserAgent(options)

  await waitForNewRelic()
  setupGlobalErrorHandlers()
}
